import { CUSTOM_GRID_SIZE, type Slot } from "../../../shared/shop-catalog.js";
import { CUSTOM_KEY } from "../../config";
import { makeEmptyCells } from "../entities/CustomArt";
import type { Settings } from "./Settings";

/** The cosmetic slots the CUSTOM editor lets a player draw. */
export const CUSTOM_SLOTS = [
  "character",
  "hat",
  "banner",
  "trail",
  "frame",
  "background",
  "explosion",
] as const satisfies readonly Slot[];

export type CustomSlot = (typeof CUSTOM_SLOTS)[number];

export interface CustomDesign {
  cells: string[];
  grid: number;
  /** Banner/frame only — whether the cosmetic animates. */
  animated?: boolean;
  /** Frame only — the colors the animated border flows through. */
  animColors?: string[];
}

interface CustomData {
  name: string;
  designs: Partial<Record<CustomSlot, CustomDesign>>;
  equipped: Partial<Record<CustomSlot, boolean>>;
}

/** The local id a custom design is equipped under (never reaches the server). */
export function customIdFor(slot: CustomSlot): string {
  return `custom.u.${slot}`;
}

/**
 * Client-side store for the player's hand-drawn cosmetics. Each player has at
 * most one editable design per slot, plus a flag for whether it's equipped.
 * Persisted to localStorage, namespaced by player name, so it never touches the
 * server (the CUSTOM collection's access flag is what's server-backed).
 */
export class CustomStore {
  private data: CustomData;

  constructor(
    private readonly settings: Settings,
    private readonly storage: Storage = window.localStorage,
  ) {
    this.data = this.load();
  }

  /** Re-read for the current player (call after login / logout). */
  reload(): void {
    this.data = this.load();
  }

  design(slot: CustomSlot): CustomDesign | null {
    return this.data.designs[slot] ?? null;
  }

  /** The design's cells, or a fresh blank grid if none exists yet. */
  cellsOrBlank(slot: CustomSlot): { cells: string[]; grid: number } {
    const d = this.data.designs[slot];
    if (d) return { cells: d.cells.slice(), grid: d.grid };
    return { cells: makeEmptyCells(CUSTOM_GRID_SIZE), grid: CUSTOM_GRID_SIZE };
  }

  setDesign(
    slot: CustomSlot,
    cells: string[],
    grid: number,
    animated = false,
    animColors: string[] = [],
  ): void {
    this.data.designs[slot] = { cells: cells.slice(), grid, animated, animColors: animColors.slice() };
    this.save();
  }

  isEquipped(slot: CustomSlot): boolean {
    return Boolean(this.data.equipped[slot] && this.data.designs[slot]);
  }

  setEquipped(slot: CustomSlot, on: boolean): void {
    this.data.equipped[slot] = on;
    this.save();
  }

  clear(): void {
    this.data = { name: this.settings.playerName, designs: {}, equipped: {} };
    try {
      this.storage.removeItem(CUSTOM_KEY);
    } catch {
      // ignore
    }
  }

  private load(): CustomData {
    const empty: CustomData = { name: this.settings.playerName, designs: {}, equipped: {} };
    if (!this.settings.isLoggedIn()) return empty;
    try {
      const raw = this.storage.getItem(CUSTOM_KEY);
      if (!raw) return empty;
      const parsed = JSON.parse(raw) as Partial<CustomData>;
      if (parsed?.name !== this.settings.playerName) return empty;
      const designs: Partial<Record<CustomSlot, CustomDesign>> = {};
      for (const slot of CUSTOM_SLOTS) {
        const d = parsed.designs?.[slot];
        if (d && Array.isArray(d.cells) && typeof d.grid === "number") {
          designs[slot] = {
            cells: d.cells.map((c) => (typeof c === "string" ? c : "")),
            grid: d.grid,
            animated: Boolean(d.animated),
            animColors: Array.isArray(d.animColors)
              ? d.animColors.filter((c): c is string => typeof c === "string" && c.length > 0)
              : [],
          };
        }
      }
      const equipped: Partial<Record<CustomSlot, boolean>> = {};
      for (const slot of CUSTOM_SLOTS) equipped[slot] = Boolean(parsed.equipped?.[slot]);
      return { name: this.settings.playerName, designs, equipped };
    } catch {
      return empty;
    }
  }

  private save(): void {
    if (!this.settings.isLoggedIn()) return;
    try {
      this.data.name = this.settings.playerName;
      this.storage.setItem(CUSTOM_KEY, JSON.stringify(this.data));
    } catch {
      // ignore (private mode / quota)
    }
  }
}
